/**
 * EIP-1193 + EIP-6963 layer over the browser's wallets, with WalletConnect
 * (`./walletConnect`, imported lazily) behind the same interface.
 *
 * Wallets are found by their EIP-6963 announcements, never by grabbing
 * `window.ethereum`: with several extensions installed, the last one injected
 * owns that property, and the click would prompt a wallet the user never chose.
 *
 * Every provider failure becomes a `WalletError`. Code 4001 (user rejected) is
 * an outcome and never reaches the error reporter: callers branch on
 * `isUserRejected()`.
 */

import { isAddress, toHex, type Address, type Hex } from "viem";
import { storageKeys, timing } from "../config";
import { addChainParams, toChainIdHex } from "./chain";
import { chainEntryFor } from "./chainSelect";
import {
  abandonWalletConnect,
  clearWalletConnectUnclean,
  closeWalletConnectModal,
  disconnectWalletConnect,
  enableWalletConnect,
  ensureWalletConnectChain,
  getWalletConnectProvider,
  hangUpOrphan,
  hasWalletConnectSession,
  initWalletConnect,
  isWalletConnectExpiry,
  isWalletConnectRejection,
  isWalletConnectReset,
  onWalletConnectDisconnected,
  orphanHangUpsSettled,
  resetWalletConnect,
  sessionGrantsSigning,
  walletConnectGeneration,
  walletConnectTeardownUnclean,
  WalletConnectUnavailable,
  WALLETCONNECT_EXPIRED,
  WALLETCONNECT_RDNS,
  WALLETCONNECT_TIMEOUT,
  WALLETCONNECT_UNAVAILABLE,
  type WalletConnectProvider,
} from "./walletConnect";

export interface Eip1193Provider {
  request(args: { method: string; params?: readonly unknown[] }): Promise<unknown>;
  on?(event: string, listener: (...args: never[]) => void): void;
  removeListener?(event: string, listener: (...args: never[]) => void): void;
  /** MetaMask marker — only read by the pre-6963 fallback. */
  isMetaMask?: boolean;
  /** Pre-6963 multi-wallet shim: every injected provider on the page. */
  providers?: readonly Eip1193Provider[];
}

/** `info` half of an `eip6963:announceProvider` event. */
export interface Eip6963ProviderInfo {
  uuid: string;
  name: string;
  /** Reverse-DNS id, e.g. "io.metamask" — the stable key we pin on. */
  rdns: string;
  /** Per EIP-6963 always a `data:` URI, so it is safe as an `<img src>`. */
  icon: string;
}

export interface Eip6963ProviderDetail {
  info: Eip6963ProviderInfo;
  provider: Eip1193Provider;
}

declare global {
  interface Window {
    ethereum?: Eip1193Provider;
  }
}

/** EIP-1193 "user rejected the request". */
export const USER_REJECTED = 4001;
/** EIP-1193 "request already pending" — a prompt is open in the wallet. */
export const REQUEST_PENDING = -32002;
/** EIP-3085/3326 "chain not added to the wallet yet". */
export const CHAIN_NOT_ADDED = 4902;
/** Synthetic code used when no injected provider exists. */
export const NO_WALLET = "NO_WALLET";
/** Synthetic code: the wallet is locked / this origin is not authorised. */
export const WALLET_LOCKED = "WALLET_LOCKED";
/** Synthetic code: the descriptor names no chain, so there is nothing to sign in to. */
export const CHAIN_UNKNOWN = "CHAIN_UNKNOWN";
export const CHAIN_UNKNOWN_MESSAGE = "The network is not known yet: reload the page.";

/** localStorage key holding the rdns of the wallet the user picked. */
export const SELECTED_RDNS_KEY = storageKeys.walletRdns;

export {
  WALLETCONNECT_EXPIRED,
  WALLETCONNECT_RDNS,
  WALLETCONNECT_TIMEOUT,
  WALLETCONNECT_UNAVAILABLE,
} from "./walletConnect";

type Unsubscribe = () => void;

const ANNOUNCE_EVENT = "eip6963:announceProvider";
const REQUEST_EVENT = "eip6963:requestProvider";

export const PENDING_MESSAGE =
  "A wallet prompt is already open — finish it in your wallet window";
export const LOCKED_MESSAGE = "Unlock your wallet and try again";

export class WalletError extends Error {
  readonly code: number | string;

  constructor(code: number | string, message: string) {
    super(message);
    this.name = "WalletError";
    this.code = code;
  }
}

export const CONNECTION_CANCELLED_MESSAGE = "Connection cancelled";

/**
 * The user backed out of a WalletConnect connection before any wallet was asked
 * anything (the QR sheet's X, Cancel, or a sign-out mid-pairing). Code 4001, so
 * it is never reported; its own class so the UI does not say "Signature
 * rejected" when no signature was requested.
 */
export class ConnectionCancelled extends WalletError {
  constructor() {
    super(USER_REJECTED, CONNECTION_CANCELLED_MESSAGE);
    this.name = "ConnectionCancelled";
  }
}

export function isConnectionCancelled(err: unknown): boolean {
  return err instanceof ConnectionCancelled;
}

/** Reads `code`/`message` off an unknown provider rejection. */
export function toWalletError(err: unknown): WalletError {
  if (err instanceof WalletError) return err;
  if (err !== null && typeof err === "object") {
    const e = err as { code?: unknown; message?: unknown };
    const code =
      typeof e.code === "number" || typeof e.code === "string" ? e.code : "WALLET_ERROR";
    const message = typeof e.message === "string" ? e.message : "Wallet request failed";
    return new WalletError(code, message);
  }
  return new WalletError("WALLET_ERROR", String(err));
}

function codeOf(err: unknown): unknown {
  if (err === null || typeof err !== "object") return undefined;
  return (err as { code?: unknown }).code;
}

/** Some wallets nest the real code (`{ code: -32603, data: { code: 4001 } }`). */
function nestedCodeOf(err: unknown): unknown {
  if (err === null || typeof err !== "object") return undefined;
  return (err as { data?: { code?: unknown } }).data?.code;
}

/** True when the failure is the user declining in their wallet (never reported). */
export function isUserRejected(err: unknown): boolean {
  return codeOf(err) === USER_REJECTED || nestedCodeOf(err) === USER_REJECTED;
}

/**
 * True when a connect prompt is already open in the wallet (-32002). A second
 * `eth_requestAccounts` then does nothing, so the user is told to finish it.
 */
export function isRequestPending(err: unknown): boolean {
  return codeOf(err) === REQUEST_PENDING || nestedCodeOf(err) === REQUEST_PENDING;
}

/** True when the wallet is locked / has not authorised this origin. */
export function isWalletLocked(err: unknown): boolean {
  return codeOf(err) === WALLET_LOCKED;
}

/** Everything except a user rejection is worth reporting. */
export function isReportableWalletError(err: unknown): boolean {
  return !isUserRejected(err);
}

function truncate(value: string, max: number): string {
  return value.length <= max ? value : `${value.slice(0, max - 1)}…`;
}

/**
 * One line for the inline banner: a plain-English instruction for the cases a
 * user can act on, otherwise the provider's own `code` + `message` truncated —
 * a real diagnostic beats "something went wrong".
 */
export function explainWalletError(err: unknown): string {
  const walletError = toWalletError(err);
  if (walletError.code === NO_WALLET) {
    return "No wallet detected — install MetaMask, then reload this page";
  }
  // These carry finished user-facing English; the default branch below
  // would prefix them with "Wallet error WALLETCONNECT_…:".
  if (
    walletError.code === WALLETCONNECT_UNAVAILABLE ||
    walletError.code === WALLETCONNECT_TIMEOUT ||
    walletError.code === WALLETCONNECT_EXPIRED ||
    walletError.code === CHAIN_UNKNOWN
  ) {
    return walletError.message;
  }
  if (isRequestPending(walletError)) return PENDING_MESSAGE;
  if (isWalletLocked(walletError)) return LOCKED_MESSAGE;
  if (isConnectionCancelled(walletError)) return CONNECTION_CANCELLED_MESSAGE;
  if (isUserRejected(walletError)) return "Request rejected in your wallet — try again";
  return `Wallet error ${String(walletError.code)}: ${truncate(walletError.message, 120)}`;
}

/**
 * Some wallets refuse to connect (or silently degrade) on a non-secure origin.
 * The UI surfaces this as a note rather than blocking the attempt.
 */
export function isInsecureOrigin(): boolean {
  return typeof window !== "undefined" && window.isSecureContext === false;
}

/* --------------------------------------------------------- EIP-6963 discovery */

/** Announced wallets, keyed by rdns — first announce for an rdns wins. */
const announced = new Map<string, Eip6963ProviderDetail>();
const discoveryListeners = new Set<() => void>();
let announceListenerInstalled = false;

function parseAnnouncement(detail: unknown): Eip6963ProviderDetail | null {
  if (detail === null || typeof detail !== "object") return null;
  const { info, provider } = detail as { info?: unknown; provider?: unknown };
  if (info === null || typeof info !== "object") return null;
  if (provider === null || typeof provider !== "object") return null;
  if (typeof (provider as Eip1193Provider).request !== "function") return null;
  const { uuid, name, rdns, icon } = info as Record<string, unknown>;
  if (typeof rdns !== "string" || rdns === "") return null;
  if (typeof name !== "string" || name === "") return null;
  return {
    info: {
      uuid: typeof uuid === "string" ? uuid : rdns,
      name,
      rdns,
      icon: typeof icon === "string" ? icon : "",
    },
    provider: provider as Eip1193Provider,
  };
}

function handleAnnounce(event: Event): void {
  const parsed = parseAnnouncement((event as CustomEvent<unknown>).detail);
  // Dedupe by rdns: wallets re-announce on every `eip6963:requestProvider`.
  if (parsed === null || announced.has(parsed.info.rdns)) return;
  announced.set(parsed.info.rdns, parsed);
  for (const listener of discoveryListeners) listener();
}

/**
 * Installed at module load, not on first use: wallets announce themselves once
 * at page load, and an announcement that arrives before we listen is lost.
 */
function installAnnounceListener(): void {
  if (announceListenerInstalled || typeof window === "undefined") return;
  window.addEventListener(ANNOUNCE_EVENT, handleAnnounce);
  announceListenerInstalled = true;
}

installAnnounceListener();

/** Snapshot of the wallets announced so far, in announcement order. */
export function listProviders(): Eip6963ProviderDetail[] {
  return [...announced.values()];
}

/**
 * Asks every injected wallet to announce itself and resolves with the list.
 * Wallets answer synchronously inside `dispatchEvent`, but a few do it from a
 * task, so we yield once before reading.
 */
export async function discoverProviders(): Promise<Eip6963ProviderDetail[]> {
  if (typeof window === "undefined") return [];
  installAnnounceListener();
  window.dispatchEvent(new Event(REQUEST_EVENT));
  await new Promise<void>((resolve) => {
    setTimeout(resolve, 0);
  });
  return listProviders();
}

/** Notifies when a new wallet announces itself. Returns an unsubscribe. */
export function onProvidersChanged(listener: () => void): () => void {
  discoveryListeners.add(listener);
  return () => {
    discoveryListeners.delete(listener);
  };
}

/** Drops discovered providers and the active provider (tests only). */
export function resetProviderDiscovery(): void {
  announced.clear();
  discoveryListeners.clear();
  readyListeners.clear();
  active = null;
  connectInFlight = null;
  walletConnectInFlight = null;
  selectedRdnsMirror = undefined;
  resetWalletConnect();
}

/**
 * In-memory copy of the pin, authoritative once anything was picked this page
 * load: `undefined` means ask storage, `null` means unset (a failed
 * `removeItem` may have left a stale value behind). Without it, a browser whose
 * storage writes throw would send requests to a wallet the user never picked.
 */
let selectedRdnsMirror: string | null | undefined = undefined;

/** rdns of the wallet the user pinned, or null. */
export function getSelectedRdns(): string | null {
  if (selectedRdnsMirror !== undefined) return selectedRdnsMirror;
  try {
    return localStorage.getItem(storageKeys.walletRdns);
  } catch {
    // Storage unavailable (privacy mode) and nothing picked yet this load.
    return null;
  }
}

/**
 * Pins the user's wallet choice so later sessions skip the picker. Pass null
 * to forget it.
 */
export function selectProvider(rdns: string | null): void {
  active = null;
  selectedRdnsMirror = rdns;
  try {
    if (rdns === null) localStorage.removeItem(storageKeys.walletRdns);
    else localStorage.setItem(storageKeys.walletRdns, rdns);
  } catch {
    // Storage unavailable — the mirror above is what holds the choice, for
    // this page load only.
  }
}

/** The announced detail for `rdns`, or null when that wallet is not present. */
export function findProvider(rdns: string): Eip6963ProviderDetail | null {
  return announced.get(rdns) ?? null;
}

/* ---------------------------------------------------------- provider selection */

/**
 * The provider requests go to, and the rdns it was resolved from (null for
 * `window.ethereum`). `picked`: the user named this wallet, so it keeps every
 * request until another pick or a reload; otherwise it gives way to the
 * remembered wallet as soon as that one can be reached (`activeIsStale`).
 */
let active: { provider: Eip1193Provider; rdns: string | null; picked: boolean } | null = null;

/** Pre-6963 fallback: a multi-wallet shim exposes `window.ethereum.providers`. */
function legacyProvider(): Eip1193Provider | undefined {
  const injected = typeof window !== "undefined" ? window.ethereum : undefined;
  if (injected === undefined) return undefined;
  const nested = injected.providers;
  if (Array.isArray(nested) && nested.length > 0) {
    return nested.find((p) => p.isMetaMask === true) ?? nested[0] ?? injected;
  }
  return injected;
}

/**
 * The WalletConnect provider is created asynchronously, by `connect()` or by
 * `restoreWalletSession()` at boot, so none here means the session is gone.
 */
function walletConnectTarget(): { provider: Eip1193Provider; rdns: string } {
  const wc = getWalletConnectProvider();
  if (wc === null) {
    throw new WalletError(
      WALLETCONNECT_UNAVAILABLE,
      "WalletConnect is not connected — sign in again",
    );
  }
  return { provider: wc, rdns: WALLETCONNECT_RDNS };
}

/**
 * Explicit choice → pin → the first announced wallet → `window.ethereum` →
 * NO_WALLET. The two fallbacks only stand in for a pin that has not announced
 * yet (`activeIsStale` drops them once it has). Synchronous, so `connect()`
 * reaches `eth_requestAccounts` with nothing awaited.
 */
function resolveTarget(rdns?: string): { provider: Eip1193Provider; rdns: string | null } {
  if (rdns !== undefined) {
    if (rdns === WALLETCONNECT_RDNS) return walletConnectTarget();
    const chosen = announced.get(rdns);
    if (chosen !== undefined) return { provider: chosen.provider, rdns };
  }
  const pinned = getSelectedRdns();
  // A WalletConnect pin throws rather than falling through to an injected
  // wallet the user never picked: a `cancelOrder` or an approval sent from the
  // wrong account cannot be undone. `connect()` branches before reaching here.
  if (pinned === WALLETCONNECT_RDNS) return walletConnectTarget();
  if (pinned !== null) {
    const detail = announced.get(pinned);
    if (detail !== undefined) return { provider: detail.provider, rdns: pinned };
  }
  const first = announced.values().next();
  if (first.done !== true) {
    return { provider: first.value.provider, rdns: first.value.info.rdns };
  }

  const legacy = legacyProvider();
  if (legacy !== undefined) return { provider: legacy, rdns: null };

  throw new WalletError(NO_WALLET, "No injected EIP-1193 wallet detected");
}

export function resolveProvider(rdns?: string): Eip1193Provider {
  return resolveTarget(rdns).provider;
}

/** True when any wallet is reachable — announced or legacy-injected. */
export function hasWallet(): boolean {
  if (announced.size > 0) return true;
  return typeof window !== "undefined" && window.ethereum !== undefined;
}

/** The pinned wallet's provider when it can be reached now, else null. */
function rememberedProvider(): Eip1193Provider | null {
  const pinned = getSelectedRdns();
  if (pinned === null) return null;
  if (pinned === WALLETCONNECT_RDNS) return getWalletConnectProvider();
  return announced.get(pinned)?.provider ?? null;
}

/**
 * True when `active` is no longer what resolution would pick (for a pick: no
 * longer live), so a reloaded extension never leaves requests on a detached
 * provider.
 */
function activeIsStale(): boolean {
  if (active === null) return true;
  // A stand-in gives way to the pin once that wallet can be reached, or a
  // wallet announcing late never takes over and signatures go to one the user
  // did not choose. A pick is never displaced: the user named it on purpose.
  if (!active.picked) {
    const remembered = rememberedProvider();
    if (remembered !== null && remembered !== active.provider) return true;
  }
  // WalletConnect is outside the EIP-6963 registry: its live provider is the
  // singleton's (nulled by a teardown), and the legacy test below would
  // discard every session.
  if (active.rdns === WALLETCONNECT_RDNS) return getWalletConnectProvider() !== active.provider;
  if (active.rdns !== null) return announced.get(active.rdns)?.provider !== active.provider;
  return legacyProvider() !== active.provider;
}

/**
 * The provider every request goes through. Seating it in `active` is required:
 * `rpc()`, `signMessage()` and `switchToChain()` branch on `active?.rdns`. The
 * early `active = null` keeps a throw from leaving a stale entry.
 */
function currentProvider(): Eip1193Provider {
  if (active !== null && !activeIsStale()) return active.provider;
  active = null;
  const target = resolveTarget();
  active = { ...target, picked: false };
  return target.provider;
}

async function request<T>(
  provider: Eip1193Provider,
  method: string,
  params?: readonly unknown[],
): Promise<T> {
  try {
    return (await provider.request(
      params === undefined ? { method } : { method, params },
    )) as T;
  } catch (err) {
    throw toWalletError(err);
  }
}

/**
 * Methods that wait on a human tapping a wallet app. The rest are
 * machine-speed and a 20 s silence means the relay is gone, not that someone
 * is reading.
 */
const WC_USER_ACTION_METHODS = new Set([
  "personal_sign",
  "eth_signTypedData_v4",
  "eth_sendTransaction",
  "wallet_switchEthereumChain",
]);

function withDeadline<T>(
  pending: Promise<T>,
  ms: number,
  onTimeout: () => WalletError,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(onTimeout()), ms);
    pending.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err: unknown) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}

/**
 * Over WalletConnect a request is a relay round-trip to a phone with nothing on
 * screen, so it gets a deadline (the library's own TTL is minutes, with no
 * cancel); an injected wallet's popup is on screen and gets none. Each
 * WalletConnect request first checks the provider's chain, which a wallet that
 * approved extra chains can move: `ensureWalletConnectChain` switches back
 * locally, never prompting, or nothing is sent. `wallet_switchEthereumChain`
 * is exempt: it is the move to the chain.
 */
function rpc<T>(method: string, params?: readonly unknown[]): Promise<T> {
  const provider = currentProvider(); // resolves and re-seats `active`
  if (active?.rdns !== WALLETCONNECT_RDNS) return request<T>(provider, method, params);
  const wc = provider as WalletConnectProvider;
  const pending =
    method === "wallet_switchEthereumChain"
      ? request<T>(wc, method, params)
      : ensureWalletConnectChain(wc).then(async (onChain) => {
          if (!onChain) throw wrongChainError();
          return request<T>(wc, method, params);
        });
  return withDeadline(
    pending,
    WC_USER_ACTION_METHODS.has(method) ? timing.wallet.walletConnectUserActionMs : timing.wallet.walletConnectReadMs,
    () =>
      new WalletError(WALLETCONNECT_TIMEOUT, "Open your wallet app to continue, then try again"),
  );
}

/**
 * The session is on another network with no account on this chain. Reported on
 * purpose: the pairing proposed this chain alone, so it is an anomaly.
 */
function wrongChainError(): WalletError {
  return new WalletError(
    WALLETCONNECT_UNAVAILABLE,
    "Your wallet app is on another network and this session cannot switch back — sign out and connect WalletConnect again",
  );
}

/** rdns of the provider `rpc()` would use right now, resolving it if needed. */
function activeRdns(): string | null {
  try {
    currentProvider();
  } catch {
    return null;
  }
  return active?.rdns ?? null;
}

function normaliseAddress(value: unknown): string {
  if (typeof value !== "string" || !isAddress(value, { strict: false })) {
    throw new WalletError("BAD_ADDRESS", "Wallet returned an invalid address");
  }
  return value.toLowerCase();
}

function firstAccount(value: unknown): unknown {
  return Array.isArray(value) ? (value as unknown[])[0] : undefined;
}

/* ------------------------------------------------------------------- requests */

/** Non-null while an `eth_requestAccounts` prompt of ours is outstanding. */
let connectInFlight: Promise<string> | null = null;
/**
 * The WalletConnect attempt, when that is what `connectInFlight` holds. Lets
 * `cancelConnect()` release the guard for a WalletConnect attempt only — never
 * for an injected prompt that is genuinely open in an extension window.
 */
let walletConnectInFlight: Promise<string> | null = null;

async function requestAccounts(provider: Eip1193Provider): Promise<string> {
  // First statement, no `await` above it — see `connect`.
  const accounts = await request<unknown>(provider, "eth_requestAccounts");
  let account = firstAccount(accounts);
  if (account === undefined) {
    // A locked wallet (or one that has not authorised this origin) resolves
    // eth_requestAccounts with an empty list instead of rejecting.
    const authorised = await request<unknown>(provider, "eth_accounts").catch(
      () => undefined,
    );
    if (Array.isArray(authorised) && authorised.length === 0) {
      throw new WalletError(WALLET_LOCKED, LOCKED_MESSAGE);
    }
    account = firstAccount(authorised);
    if (account === undefined) {
      throw new WalletError("NO_ACCOUNTS", "Wallet returned no accounts");
    }
  }
  return normaliseAddress(account);
}

/**
 * WalletConnect connect through `enable()`, the call that pairs. No deadline:
 * the QR sheet has its own close button, and unlocking a phone takes time.
 * `explicit`: the user tapped WalletConnect, not only the pin. `onPairing`
 * fires just before `enable()` opens the QR sheet, never for a restored session.
 */
async function connectWalletConnect(
  explicit: boolean,
  onPairing?: () => void,
): Promise<string> {
  let wc: WalletConnectProvider;
  let accounts: string[];
  try {
    // A provider dropped by an earlier Cancel may have paired after all: until
    // its hang-up lands, its session sits in the shared storage, and the init
    // below would rehydrate it and sign in with no QR code. Awaited only when
    // pending, so the init otherwise starts synchronously and captures the
    // teardown generation first; a teardown during the wait is caught by
    // comparing generations.
    const orphansBeforeInit = orphanHangUpsSettled();
    if (orphansBeforeInit !== null) {
      const teardowns = walletConnectGeneration();
      await orphansBeforeInit;
      if (walletConnectGeneration() !== teardowns) {
        throw new WalletConnectUnavailable("WalletConnect session was closed", "closed");
      }
    }
    wc = await initWalletConnect();
    // A sign-out that could not reach the relay leaves the pairing in the
    // library's store, and `init()` rehydrates it: `enable()` would return the
    // previous user's account with no QR code. Hang up first.
    if (walletConnectTeardownUnclean() && hasWalletConnectSession(wc)) {
      await wc.disconnect().catch(() => undefined);
      // The relay may still be unreachable. Refuse rather than sign in as the
      // previous user; a retry once the relay answers costs one tap.
      if (hasWalletConnectSession(wc)) {
        throw new WalletError(
          WALLETCONNECT_UNAVAILABLE,
          "Could not end the previous WalletConnect session — check your connection and try again",
        );
      }
    }
    // Again for an orphan that paired during the init: its `disconnect()`
    // expires every pending pairing in the shared Core, which must happen
    // before `enable()` creates this one.
    const orphansBeforeEnable = orphanHangUpsSettled();
    if (orphansBeforeEnable !== null) await orphansBeforeEnable;
    // Cancelled or signed out meanwhile: the teardown dropped this provider,
    // and `enable()` would open a QR sheet nothing can close any more.
    if (getWalletConnectProvider() !== wc) throw new ConnectionCancelled();
    // `enable()` short-circuits on a session and shows nothing then.
    if (!hasWalletConnectSession(wc)) onPairing?.();
    try {
      accounts = await enableWalletConnect(wc);
    } catch (err) {
      // Classified by the outer catch; this only makes sure the attempt the
      // user just gave up on cannot come back as a silent sign-in.
      dropUnpairedAttempt(wc);
      throw err;
    }
  } catch (err) {
    if (err instanceof ConnectionCancelled) throw err;
    if (err instanceof WalletConnectUnavailable) {
      if (err.reason === "closed") {
        // A Cancel or sign-out overtook the init: never reported, and the pin
        // stays.
        throw new ConnectionCancelled();
      }
      // No project id any more. A pin-only connect forgets the pin, so the
      // next click reaches an injected wallet; an explicit pick keeps it and
      // is owed the reason.
      if (!explicit) selectProvider(null);
      throw new WalletError(WALLETCONNECT_UNAVAILABLE, err.message);
    }
    // A closed QR sheet (its X, or Cancel) involved no wallet: "cancelled",
    // never "rejected".
    if (isWalletConnectReset(err)) throw new ConnectionCancelled();
    if (isUserRejected(err) || isWalletConnectRejection(err)) {
      // The wallet declined the proposal (EIP-1193 4001, WalletConnect 5000).
      throw new WalletError(USER_REJECTED, "Request rejected in your wallet — try again");
    }
    if (isWalletConnectExpiry(err)) {
      throw new WalletError(WALLETCONNECT_EXPIRED, "The connection request expired — try again");
    }
    // Everything else (a dead relay, a blocked WSS origin, an unpropagated
    // project id) stays reportable.
    throw toWalletError(err);
  }
  if (getWalletConnectProvider() !== wc) {
    // Signed out or cancelled while pairing, and the phone approved anyway.
    // Hang up, or the phone keeps a session nothing here can end; the hang-up
    // is shared with the late-session watcher. Never reported.
    void hangUpOrphan(wc);
    throw new ConnectionCancelled();
  }
  // This pairing is ours and was established just now, whatever state an
  // earlier teardown left behind. Not before the check above: an orphan's
  // session is not ours to vouch for.
  clearWalletConnectUnclean();
  // Imported lazily for the import cycle described in walletConnect.ts.
  const { getChainId: deploymentChainId } = await import("./runtimeConfig");
  const chainId = deploymentChainId();
  if (chainId === null) {
    await disconnectWalletConnect();
    throw new WalletError(CHAIN_UNKNOWN, CHAIN_UNKNOWN_MESSAGE);
  }
  if (!sessionGrantsSigning(wc, chainId)) {
    // The proposal carries no required namespace, so a wallet may approve a
    // subset. Without this the user signs in and then hits "Missing or
    // invalid. request() method" on their first order, minutes later.
    await disconnectWalletConnect();
    throw new WalletError(
      WALLETCONNECT_UNAVAILABLE,
      "Your wallet did not allow order signing — use a wallet that supports eth_signTypedData_v4",
    );
  }
  const account = firstAccount(accounts);
  if (account === undefined) {
    throw new WalletError("NO_ACCOUNTS", "Wallet returned no accounts");
  }
  seatActive({ provider: wc, rdns: WALLETCONNECT_RDNS, picked: explicit });
  return normaliseAddress(account);
}

/**
 * Prompts for account access and resolves with the primary account, lowercased.
 * `rdns` picks a wallet, else the pin. Nothing may be awaited before
 * `eth_requestAccounts`: an extension opens its popup only while the click's
 * user gesture is on the stack, else it opens in a tab and nothing happens.
 * `onPairing` is WalletConnect-only (see `connectWalletConnect`).
 */
export async function connect(rdns?: string, onPairing?: () => void): Promise<string> {
  if (connectInFlight !== null) {
    // The wallet would swallow a second prompt as -32002: a dead button.
    throw new WalletError(REQUEST_PENDING, PENDING_MESSAGE);
  }
  // WalletConnect may await before prompting: its modal is in-page, with no
  // popup gesture to keep alive. Each `finally` clears the guard only while it
  // is still this attempt's: `cancelConnect()` releases it early, and a later
  // attempt's guard must survive the cancelled one settling.
  const chosen = rdns ?? getSelectedRdns();
  if (chosen === WALLETCONNECT_RDNS) {
    const pendingWc = connectWalletConnect(rdns === WALLETCONNECT_RDNS, onPairing);
    connectInFlight = pendingWc;
    walletConnectInFlight = pendingWc;
    try {
      return await pendingWc;
    } finally {
      if (connectInFlight === pendingWc) connectInFlight = null;
      if (walletConnectInFlight === pendingWc) walletConnectInFlight = null;
    }
  }
  const target = resolveTarget(rdns);
  const pending = requestAccounts(target.provider);
  connectInFlight = pending;
  try {
    const address = await pending;
    // Seated so later signatures cannot land on another wallet. A pick only
    // when the named wallet answered: an rdns that had not announced fell
    // through to a stand-in, which must still give way to the pin.
    seatActive({ ...target, picked: rdns !== undefined && target.rdns === rdns });
    return address;
  } finally {
    if (connectInFlight === pending) connectInFlight = null;
  }
}

/**
 * Fired when another provider is seated while the app is mounted (a
 * `connect()`, or a "pending" restore adopted late). App.tsx's `accountsChanged`
 * subscription binds to the provider resolved at mount, so without this a
 * phone-side account switch would never sign the user out. Not fired from
 * `currentProvider()`, which `subscribe()` calls: a re-subscribing listener
 * would trigger itself.
 */
const readyListeners = new Set<() => void>();

export function onWalletProviderReady(listener: () => void): Unsubscribe {
  readyListeners.add(listener);
  return () => {
    readyListeners.delete(listener);
  };
}

/** Seats `next` and announces it when it is a different provider. */
function seatActive(next: NonNullable<typeof active>): void {
  const changed = next.provider !== active?.provider;
  active = next;
  if (changed) for (const listener of [...readyListeners]) listener();
}

/** Outcome of `restoreWalletSession()`; see main.tsx. */
export type WalletRestore = "none" | "restored" | "stale" | "pending";

/**
 * Can this rehydrated session serve the address `aile.auth` was issued for?
 * Membership, not `accounts[0]`: a reordered list is the same wallet. An empty
 * list or no expected address contradicts nothing.
 */
function sessionServes(wc: WalletConnectProvider, expected?: string | null): boolean {
  if (expected === undefined || expected === null) return true;
  const accounts = (wc.accounts ?? []).map((a) => a.toLowerCase());
  return accounts.length === 0 || accounts.includes(expected.toLowerCase());
}

/**
 * Boot hook, awaited before the router mounts: re-attaches a WalletConnect
 * session this browser still holds, never prompting (`init()` rehydrates it;
 * `enable()` is not called). Only a WalletConnect pin pays the import.
 *
 * "stale": pinned with no usable session (deleted on the phone, expired, no
 * project id, or moved to an account other than `expected`, a change no event
 * reports to a closed tab). The backend session may still be valid, but
 * nothing could sign or read, so the provider is dropped and main.tsx signs
 * the user out.
 *
 * "pending": the deadline passed, which on a slow connection is a healthy
 * session still loading. It is kept and adopted on arrival; treating it as
 * stale would hang up the pairing and force a QR re-scan on every cold load.
 */
export async function restoreWalletSession(expected?: string | null): Promise<WalletRestore> {
  if (getSelectedRdns() !== WALLETCONNECT_RDNS) return "none";
  let wc: WalletConnectProvider | null = null;
  let timedOut = false;
  try {
    wc = await withDeadline(initWalletConnect(), timing.wallet.restoreMs, () => {
      timedOut = true;
      return new WalletError(WALLETCONNECT_TIMEOUT, "WalletConnect did not answer");
    });
  } catch {
    // No project id, a dead relay, a cleared session: handled below.
    wc = null;
  }
  if (wc !== null && hasWalletConnectSession(wc) && !walletConnectTeardownUnclean()) {
    if (sessionServes(wc, expected)) {
      active = { provider: wc, rdns: WALLETCONNECT_RDNS, picked: false };
      return "restored";
    }
  }
  if (timedOut) {
    // Adopt the provider when the init already in flight settles (the same
    // promise, so free); meanwhile `rpc()` fails with WALLETCONNECT_UNAVAILABLE,
    // which is recoverable. Nothing is torn down.
    void initWalletConnect().then(
      (late) => {
        if (active !== null || !hasWalletConnectSession(late)) return;
        // A connect started on /login meanwhile owns this provider and runs
        // its own unclean hang-up; tearing it down here would fail that click.
        if (connectInFlight !== null) return;
        // The fast path's tests, or the outcome would depend on connection
        // speed. Not seating `active` is not enough, since `resolveTarget()`
        // reads the singleton: the pairing is ended and the user signed out,
        // as a phone-side `session_delete` does.
        if (walletConnectTeardownUnclean() || !sessionServes(late, expected)) {
          void abandonWalletConnect();
          return;
        }
        seatActive({ provider: late, rdns: WALLETCONNECT_RDNS, picked: false });
      },
      () => undefined,
    );
    return "pending";
  }
  // Bumps the generation, so a provider still being built by an init we have
  // given up on is discarded (and hung up on) rather than adopted later.
  await disconnectWalletConnect();
  active = null;
  return "stale";
}

/**
 * After a failed `enable()` with no session, drops the provider as Cancel does,
 * so `disconnectWalletConnect` arms `watchForLateSession` on it.
 *
 * Workaround: on the QR sheet's X, ethereum-provider 2.25.0 rejects
 * `connect()`, but universal-provider's `abortPairingAttempt()` is a no-op and
 * the pairing keeps waiting. A phone approving later gives this provider a
 * session, and the next click's `enable()` would sign in with no QR code. A
 * provider with a session is left alone (only the `eth_requestAccounts` after
 * pairing failed), as is one a teardown already dropped. Synchronous up to the
 * watcher, so it is armed before anyone can click again.
 */
function dropUnpairedAttempt(wc: WalletConnectProvider): void {
  if (getWalletConnectProvider() !== wc || hasWalletConnectSession(wc)) return;
  if (active?.provider === wc) active = null;
  void disconnectWalletConnect();
}

function tearDownWalletConnect(): void {
  if (active?.rdns === WALLETCONNECT_RDNS) active = null;
  void disconnectWalletConnect();
}

/**
 * Ends the wallet session: in practice a WalletConnect pairing, which would
 * otherwise stay live on the phone. A WalletConnect pin goes too: a failed
 * teardown leaves the pairing in storage, and a pinned browser would resume it
 * at boot for the next person on a shared machine. An injected pin stays.
 * Synchronous: every caller navigates straight after.
 */
export function disconnect(): void {
  if (getSelectedRdns() === WALLETCONNECT_RDNS) selectProvider(null);
  tearDownWalletConnect();
}

/**
 * Cancel on the sign-in screen: abandons a WalletConnect pairing that never
 * completed. Not `disconnect()`: the pin survives, so the retry still names
 * WalletConnect.
 *
 * Closing the modal rejects `enable()`, which has no deadline, but does not
 * stop the pairing (`closeWalletConnectModal`), so the singleton is torn down
 * too, arming a watcher that hangs up on a late session. The guard is released
 * here because the attempt may only reject seconds later, once the factory
 * settles, and meanwhile every wallet button would answer "a prompt is already
 * open". Only a WalletConnect guard: an injected prompt stays open whatever
 * this page does. A sheet AppKit is still opening appears after this close,
 * and only its own X removes it.
 */
export function cancelConnect(): void {
  // Order matters: the modal belongs to the provider the singleton still holds.
  closeWalletConnectModal();
  tearDownWalletConnect();
  if (walletConnectInFlight !== null && connectInFlight === walletConnectInFlight) {
    connectInFlight = null;
  }
  walletConnectInFlight = null;
}

/**
 * Fires when the wallet ends the session itself (a WalletConnect pairing killed
 * on the phone). An injected wallet's EIP-1193 `disconnect` is a network blip,
 * not a sign-out, so it is deliberately not wired here.
 */
export function onWalletDisconnected(handler: () => void): Unsubscribe {
  return onWalletConnectDisconnected(handler);
}

/** Currently authorised accounts without prompting (empty when locked). */
export async function getAccounts(): Promise<string[]> {
  // `hasWallet()` sees only injected wallets, and on a phone the pairing is the
  // only wallet there is.
  if (active === null && getWalletConnectProvider() === null && !hasWallet()) return [];
  try {
    const accounts = await rpc<unknown>("eth_accounts");
    if (!Array.isArray(accounts)) return [];
    return (accounts as unknown[])
      .filter((a): a is string => typeof a === "string")
      .map((a) => a.toLowerCase());
  } catch {
    // A locked/unavailable wallet is not an error worth surfacing here.
    return [];
  }
}

/** Chain id of the wallet's selected network, as a decimal number. */
export async function getChainId(): Promise<number> {
  const raw = await rpc<unknown>("eth_chainId");
  if (typeof raw === "number") return raw;
  if (typeof raw === "string") {
    const parsed = Number.parseInt(raw, 16);
    if (Number.isFinite(parsed)) return parsed;
  }
  throw new WalletError("BAD_CHAIN_ID", "Wallet returned an unreadable chain id");
}

/**
 * Switches the wallet to `chainId`, adding the network first when the wallet
 * does not know it (4902) and `chains.json` gives its facts. A rejection
 * propagates as 4001.
 */
export async function switchToChain(chainId: number): Promise<void> {
  const provider = currentProvider();
  try {
    await rpc<unknown>("wallet_switchEthereumChain", [{ chainId: toChainIdHex(chainId) }]);
  } catch (err) {
    const walletError = toWalletError(err);
    // Outcomes the UI renders keep their codes over any transport: a phone's
    // Reject of the relayed switch is a real 4001, never reported.
    if (isUserRejected(walletError) || isRequestPending(walletError)) throw walletError;
    if (walletError.code === WALLETCONNECT_TIMEOUT) throw walletError;
    if (active?.provider !== provider || activeIsStale()) throw new ConnectionCancelled();
    const entry = chainEntryFor(chainId);
    if (activeRdns() === WALLETCONNECT_RDNS) {
      // A paired session cannot add chains, so the 4902 recovery cannot help.
      const name = entry?.label || `chain ${chainId}`;
      throw new WalletError(
        WALLETCONNECT_UNAVAILABLE,
        `Switch your wallet to ${name} and try again`,
      );
    }
    if (walletError.code !== CHAIN_NOT_ADDED) throw walletError;
    const params = entry === undefined ? null : addChainParams(entry);
    if (params === null) throw walletError;
    await rpc<unknown>("wallet_addEthereumChain", [params]);
  }
}

/**
 * `personal_sign` of the SIWE message. Over WalletConnect it is sent as hex,
 * the canonical eip155 form, because a mobile wallet may misread a raw string;
 * injected wallets keep the plain string. The signature is the same either way.
 */
export async function signMessage(address: string, message: string): Promise<Hex> {
  const payload = activeRdns() === WALLETCONNECT_RDNS ? toHex(message) : message;
  const signature = await rpc<unknown>("personal_sign", [payload, address]);
  if (typeof signature !== "string") {
    throw new WalletError("BAD_SIGNATURE", "Wallet returned an unreadable signature");
  }
  return signature as Hex;
}

/**
 * `eth_signTypedData_v4`. The payload is passed as a JSON *string* — that is
 * what MetaMask and every wallet cloning its RPC surface expects.
 */
export async function signTypedData(address: string, typedData: unknown): Promise<Hex> {
  const signature = await rpc<unknown>("eth_signTypedData_v4", [
    address,
    JSON.stringify(typedData),
  ]);
  if (typeof signature !== "string") {
    throw new WalletError("BAD_SIGNATURE", "Wallet returned an unreadable signature");
  }
  return signature as Hex;
}

/* ------------------------------------------------------- contract read/write */

/**
 * `eth_call` through the wallet's node: the SPA has no RPC URL of its own.
 * "0x" (no contract) means unreadable, never zero. Over WalletConnect the
 * provider answers it from our `rpcMap`, not over the relay.
 */
export function ethCall(to: string, data: Hex, from?: string): Promise<Hex> {
  const tx = from === undefined ? { to, data } : { to, data, from };
  return rpc<Hex>("eth_call", [tx, "latest"]);
}

/** `eth_sendTransaction` — resolves with the transaction hash. */
export async function sendTransaction(tx: {
  from: string;
  to: string;
  data: Hex;
}): Promise<Hex> {
  const hash = await rpc<unknown>("eth_sendTransaction", [tx]);
  if (typeof hash !== "string") {
    throw new WalletError("BAD_TX_HASH", "Wallet returned an unreadable transaction hash");
  }
  return hash as Hex;
}

/**
 * Mined-transaction receipt, trimmed to what the UI branches on. `status` is
 * `unknown` on purpose: the JSON-RPC spec says "0x1"/"0x0", but wallets and
 * mock providers hand back numbers and booleans too.
 */
export interface TxReceipt {
  /** "0x1" success, "0x0" reverted. */
  status?: unknown;
  [key: string]: unknown;
}

/** `eth_getTransactionReceipt` — null while the transaction is still pending. */
export async function getTransactionReceipt(hash: Hex): Promise<TxReceipt | null> {
  const receipt = await rpc<unknown>("eth_getTransactionReceipt", [hash]);
  if (receipt === null || typeof receipt !== "object") return null;
  return receipt as TxReceipt;
}

function subscribe(event: string, listener: (...args: never[]) => void): Unsubscribe {
  let provider: Eip1193Provider | undefined;
  try {
    provider = currentProvider();
  } catch {
    // No wallet at all — nothing to subscribe to.
    return () => undefined;
  }
  if (provider.on === undefined) return () => undefined;
  provider.on(event, listener);
  return () => {
    provider?.removeListener?.(event, listener);
  };
}

/** Fires when the wallet switches/disconnects accounts. Returns an unsubscribe. */
export function onAccountsChanged(handler: (accounts: string[]) => void): Unsubscribe {
  return subscribe("accountsChanged", ((...args: unknown[]) => {
    const accounts = args[0];
    handler(
      Array.isArray(accounts)
        ? (accounts as unknown[])
            .filter((a): a is string => typeof a === "string")
            .map((a) => a.toLowerCase())
        : [],
    );
  }) as (...args: never[]) => void);
}

/** Fires when the wallet switches networks. Returns an unsubscribe. */
export function onChainChanged(handler: (chainId: number) => void): Unsubscribe {
  return subscribe("chainChanged", ((...args: unknown[]) => {
    const raw = args[0];
    if (typeof raw === "number") return handler(raw);
    if (typeof raw === "string") {
      const parsed = Number.parseInt(raw, 16);
      if (Number.isFinite(parsed)) handler(parsed);
    }
  }) as (...args: never[]) => void);
}

/** Type re-export so callers can annotate addresses without importing viem. */
export type { Address, Hex };
